/**
 * Realtime Offer Service - has become a disgusting AI jumbled mess of code, but its working for hwat you need.
 * 
 * This service handles all offer updates to the database and automatically converts
 * Market Value (MV) terms to Assessment Value (AV) terms before sending to backend.
 * 
 * Key Features:
 * - Converts all offer values from MV to AV using LOA (Level of Assessment)
 * - LOA = min(RAR, EQ) where RAR and EQ are retrieved from persisted state
 * - All database entries are now stored in AV terms
 * - Maintains backward compatibility with existing frontend logic
 * 
 * Updated: All offer values (OfferValue, PortfolioOffer, TownOffer) are converted to AV terms
 */

import logger from '../../../../utils/logger';
import axiosInstance from '../../../../fetchConfig/axiosConfig.js';
import { updateNegotiationObject } from '../utils/negotiationObjectUpdater.js';
import { usePersistedDataStore } from '../../../store';
import { isCaseSettled } from '../utils/helpers.js';
import { negotiatorIdForSettlement } from '../../Settlement/negotiatorLookup.js';
import { isTownViewDevNoPersist } from '../../../../utils/townViewDevMode.js';
import {
  batchHasMeaningfulTownOffer,
  maybeNotifyTownPortalFirstResponse,
} from '../../../../utils/townPortalFirstResponseNotify.js';
import { deleteNegotiationEntry } from './deleteNegotiationService.js';
import { getMostRecentOffer } from '../../../../utils/mostRecentOfferUtils.js';

const offerValueIsPersistable = (value) => {
  if (value === null || value === undefined || value === '' || value === 'None') return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric !== 0;
};

const incomingOfferValue = (offer) => {
  if (offer.offeredBy === 'Town') {
    return offer.TownOffer !== undefined ? offer.TownOffer : offer.offerValue;
  }
  if (offer.PortfolioOffer !== undefined && offer.OfferValue === undefined && offer.TownOffer === undefined) {
    return offer.PortfolioOffer;
  }
  return offer.OfferValue !== undefined ? offer.OfferValue : offer.offerValue;
};

const isClearingOfferValue = (offer) => !offerValueIsPersistable(incomingOfferValue(offer));

const resolveOfferIdForClear = (negotiationObj, parcelId, offeredBy) => {
  const caseData = negotiationObj?.cases?.find((c) => c.parcel_id === parcelId);
  const result = getMostRecentOffer(caseData?.Negotiations, offeredBy);
  return result?.offer?.OfferID ?? null;
};
/**
 * Determines whether to insert a new offer or update an existing one
 * Simplified logic: If there are two offers within a negotiation round, always insert
 */
export const determineInsertOrUpdate = (negotiationObj, offer, roundNumber) => {
  if (isClearingOfferValue(offer)) {
    return { action: 'clear' };
  }

  const parcelId = offer.parcelId;
  const offeredBy = offer.offeredBy;
  
  // If roundNumber is provided (e.g., for comment editing or dialog confirmation), always use that round
  if (roundNumber !== undefined && roundNumber !== null) {
    // Check if there's already an offer for this party in the specified round
    const caseData = negotiationObj.cases.find(c => c.parcel_id === parcelId);
    if (caseData && caseData.Negotiations && Array.isArray(caseData.Negotiations)) {
      const targetRound = caseData.Negotiations.find(r => r.round === roundNumber);
      // logger.log('🔍 [AV MODE DEBUG] Target round:', targetRound);
      if (targetRound && targetRound.offers) {
        const existingOffer = targetRound.offers.find(o => o.OfferedBy === offeredBy);
        // logger.log('🔍 [AV MODE DEBUG] Existing offer:', existingOffer);
        if (existingOffer) {
          const result = { action: 'update', offerId: existingOffer.OfferID };
          // logger.log('🔍 [AV MODE DEBUG] Found existing offer in specified round, result:', result);
          return result;
        }
      }
    }
    // No existing offer in the specified round, insert new one (no dialog since round was already confirmed)
    const result = { action: 'insert', round: roundNumber };
    // logger.log('🔍 [AV MODE DEBUG] No existing offer in specified round, inserting in confirmed round:', roundNumber);
    return result;
  }
  
  // Find the case in the negotiation object
  const caseData = negotiationObj.cases.find(c => c.parcel_id === parcelId);
  
  if (!caseData || !caseData.Negotiations || !Array.isArray(caseData.Negotiations) || caseData.Negotiations.length === 0) {
    const result = { action: 'insert', round: 1 };
    // logger.log('🔍 [AV MODE DEBUG] No existing negotiations, result:', result);
    return result; // No existing negotiations, insert new round 1 without dialog
  }
  
  // Find the most recent round (current round)
  const currentRound = caseData.Negotiations.length > 0 ? 
    Math.max(...caseData.Negotiations.map(r => r.round || 0)) : 1;
  
  // logger.log('🔍 [AV MODE DEBUG] Current round:', currentRound);
  
  // Get current round data
  const currentRoundData = caseData.Negotiations.find(r => r.round === currentRound);
  const currentRoundOffers = currentRoundData?.offers || [];
  const hasTwoOffersInCurrentRound = currentRoundOffers.length >= 2;

  
  // Rule 1: If current round has 2 offers, create next round with dialog
  if (hasTwoOffersInCurrentRound) {
    const nextRound = currentRound + 1;
    const result = { action: 'insert', round: nextRound, showDialog: true };
    // logger.log('🔍 [AV MODE DEBUG] Current round full (2 offers), creating next round with dialog:', nextRound);
    return result;
  }
  
  // Rule 2: Check if same party already has offer in current round
  const existingOfferBySameParty = currentRoundOffers.find(o => o.OfferedBy === offeredBy);
  
  if (existingOfferBySameParty) {
    const result = { action: 'update', offerId: existingOfferBySameParty.OfferID };
    // logger.log('🔍 [AV MODE DEBUG] Same party has offer in current round, updating:', result);
    return result;
  }
  
  // Rule 3: Different party, current round has space, insert without dialog
  const result = { action: 'insert', round: currentRound };
  // logger.log('🔍 [AV MODE DEBUG] Different party, current round has space, inserting without dialog:', result);
  return result;
};

const offerDateFromNegotiationByOfferId = (negotiationObj, parcelId, offerId) => {
  if (!negotiationObj?.cases || offerId == null) return null;
  const caseData = negotiationObj.cases.find((c) => c.parcel_id === parcelId);
  for (const r of caseData?.Negotiations || []) {
    const o = r.offers?.find((x) => String(x.OfferID ?? x.offerId) === String(offerId));
    if (o) return o.OfferDate ?? o.offerDate ?? null;
  }
  return null;
};


/**
 * Update portfolio offers in batch
 * @param {Array} updatedCases - Array of cases with portfolio offers
 * @param {Object} context - Context object with muniCode, taxYear, etc.
 * @returns {Promise<Object>} Result of the batch update
 */
export const updatePortfolioOffersBatch = async (updatedCases, context) => {
  try {
    const { muniCode, taxYear, userId, userAccess } = context;
    
    
    if (!muniCode || !taxYear || !userId) {
      throw new Error('Missing required context: muniCode, taxYear, or userId');
    }

    // Filter cases that have portfolio offers and are not settled
    const portfolioOffers = updatedCases
      .filter(caseItem => {
        // Check if case has portfolio offer
        const hasPortfolioOffer = caseItem.PortfolioOffer !== undefined && caseItem.PortfolioOffer !== null;
        
        // Check if case is settled using the isCaseSettled helper
        const isSettled = isCaseSettled(caseItem);
        
        if (isSettled) {
          // wwhat was this for???
        }
        
        return hasPortfolioOffer && !isSettled;
      })
      .map(caseItem => {
        // Determine UserType based on user access level
        const userType = userAccess?.level === 'Training' ? 'Training' : 'Aventine';
        
        logger.log(`🔄 [PORTFOLIO BATCH] Using AV values directly for ${caseItem.parcel_id}:`);
        logger.log(`   - OfferValue: AV ${caseItem.OfferValue}`);
        logger.log(`   - PortfolioOffer: AV ${caseItem.PortfolioOffer}`);
        
        return {
          parcel_id: caseItem.parcel_id,
          OfferValue: caseItem.OfferValue, // Use AV value directly
          PortfolioOffer: caseItem.PortfolioOffer, // Use AV value directly
          LiveOffer: 0, // Portfolio offers are not live until sent via email
          Negotiator: userId,
          UserType: userType,
          OfferedBy: 'Aventine' // Portfolio offers are always Aventine offers
        };
      });

    if (portfolioOffers.length === 0) {
      return {
        success: true,
        message: 'No portfolio offers to update',
        data: { updatedCount: 0 }
      };
    }

    const batchData = {
      muniCode,
      taxYear: String(taxYear),
      offers: portfolioOffers,
      userId,
      caseType: 'Town'
    };

    const response = await axiosInstance.post('/create_negotiation_offers_batch', batchData);
    
    if (response.data.success) {
      return {
        success: true,
        message: `Successfully updated ${portfolioOffers.length} portfolio offers`,
        data: response.data
      };
    } else {
      throw new Error(response.data.error || 'Failed to update portfolio offers');
    }

  } catch (error) {
    logger.error('❌ Error updating portfolio offers in batch:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to update portfolio offers'
    };
  }
};


/**
 * Batch offer update service for handling multiple offer updates in a single API call
 */

/**
 * Updates multiple offers in a single batch operation
 * @param {Array} offers - Array of offer objects to update
 * @param {Object} context - muniCode, taxYear, userId, userAccess, caseType, offerSource; optional courtDate (counterparty link scope for negotiator lookup); optional townView (Town portal: Town rows get OfferSource TownPortal on the wire). Optional issuedByNegotiatorId when townView to skip a second negotiator lookup (e.g. processSettlement after courtfiles).
 * @param {Object} options - round, liveOffer, dueDate
 * @returns {Promise<Object>} Result of the batch update
 */
export const updateOffersBatch = async (offers, context, options = {}) => {
  
  try {
    const { muniCode, taxYear, caseType = 'Town', userId, userAccess } = context;
    const { round, liveOffer = 0, dueDate } = options;

    if (!muniCode || !taxYear) {
      throw new Error('MuniCode and TaxYear are required for batch update');
    }

    if (!offers || !Array.isArray(offers) || offers.length === 0) {
      throw new Error('No offers provided for batch update');
    }

    const skipDbPersist = isTownViewDevNoPersist(context.townView);

    let negotiatorForOffers = context.issuedByNegotiatorId ?? userId;
    if (context.townView && context.issuedByNegotiatorId == null && !skipDbPersist) {
      negotiatorForOffers = await negotiatorIdForSettlement({
        ...context,
        townView: true,
        userId,
        userAccess,
      });
    }

    // Determine offered_by from the first offer
    const offeredBy = offers.length > 0 ? offers[0].offeredBy : 'Aventine';
    
    // Get current negotiation object for insert/update determination
    const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
    
    // Determine insert/update actions for each offer BEFORE sending to backend
    const offersWithActions = offers.map(offer => {
      if (isClearingOfferValue(offer)) {
        return { ...offer, determinedAction: { action: 'clear' } };
      }

      let shouldInsertOrUpdate;
      if (offer.preDeterminedAction) {
        shouldInsertOrUpdate = offer.preDeterminedAction;
        // logger.log('📋 [BATCH UPDATE] Using pre-determined action for', offer.parcelId, ':', shouldInsertOrUpdate);
      } else {
        shouldInsertOrUpdate = determineInsertOrUpdate(getNegotiationObj, offer, options.round);
        // logger.log('🔍 [BATCH UPDATE] Determined action for', offer.parcelId, ':', shouldInsertOrUpdate);
      }
      
      // For INSERT actions, remove comments to ensure new rounds start fresh
      const modifiedOffer = { ...offer };
      if (shouldInsertOrUpdate.action === 'insert') {
        // logger.log('➕ [BATCH UPDATE] INSERT action detected for', offer.parcelId, '- removing inherited comments');
        modifiedOffer.comments = undefined; // Remove inherited comments for new rounds
      } else if (
        shouldInsertOrUpdate.action === 'update' &&
        modifiedOffer.comments === undefined &&
        shouldInsertOrUpdate.offerId
      ) {
        const caseData = getNegotiationObj?.cases?.find((c) => c.parcel_id === modifiedOffer.parcelId);
        for (const round of caseData?.Negotiations || []) {
          const existing = round.offers?.find(
            (o) => String(o.OfferID ?? o.offerId) === String(shouldInsertOrUpdate.offerId),
          );
          if (existing && existing.Comments != null) {
            modifiedOffer.comments = existing.Comments;
            break;
          }
        }
      }

      return {
        ...modifiedOffer,
        determinedAction: shouldInsertOrUpdate
      };
    });

    const offersToPersist = [];
    for (const offerWithAction of offersWithActions) {
      const { determinedAction, ...offer } = offerWithAction;
      if (!isClearingOfferValue(offer)) {
        offersToPersist.push(offerWithAction);
        continue;
      }

      const offerIdToDelete = resolveOfferIdForClear(
        getNegotiationObj,
        offer.parcelId,
        offer.offeredBy,
      );

      if (offerIdToDelete) {
        await deleteNegotiationEntry({
          offerId: offerIdToDelete,
          parcelId: offer.parcelId,
          taxYear: String(taxYear),
        });
        continue;
      }

      return {
        success: false,
        error: 'No offer ID found',
      };
    }

    if (offersToPersist.length === 0) {
      return { success: true, message: 'Offer cleared', processedCount: 0, operationsCount: 0 };
    }
    
    // Prepare the batch request payload -- THIS IS WHERE I THINK THE BUG CAN BE THAT WE GET A NULL OFFERED BY VALUE.
    const batchPayload = {
      muniCode,
      taxYear,
      townView: !!context.townView,
      offerSource: context.offerSource || 'Manual',
      userId: negotiatorForOffers, // do not add shorthand `userId` below — it overwrites this
      offers: offersToPersist
        .filter((offer) => !isClearingOfferValue(offer))
        .map(offer => {
        // If you are accepting an offer, use the preexisting offer date instead of the current one to preserve the original offer sent date.
        const da = offer.determinedAction;
        const aventineAcceptUpdate = offer.offeredBy === 'Aventine' && offer.response === 'Accept' && da?.action === 'update';
        const offerDate = aventineAcceptUpdate ? offer.offerDate ||
            offerDateFromNegotiationByOfferId(getNegotiationObj, offer.parcelId, da.offerId) ||
            new Date().toISOString()
          : offer.offerDate || new Date().toISOString();
        const offerData = {
          parcel_id: offer.parcelId,
          OfferedBy: offer.offeredBy,
          OfferDate: offerDate,
          DueDate: offer.dueDate || dueDate,
          Negotiator: negotiatorForOffers,
          CaseType: caseType,
          UserType: userAccess?.level === 'Training' ? 'Training' : 'Aventine',
          Response: offer.response,
          // if the offer is coming from the town portal, set the offer source to TownPortal
          OfferSource:
            context.townView && offer.offeredBy === 'Town'
              ? 'TownPortal'
              : context.offerSource || 'Manual',
        };
        if (offer.backendRound != null) {
          offerData.Round = offer.backendRound;
        }

        // Add the appropriate offer value field based on the type
        // Now that frontend stores AV values directly, send them as-is to backend
        if (offer.offeredBy === 'Town') {
          // logger.log(`🔄 [BATCH UPDATE] Sending Town offer AV ${offer.offerValue} directly to backend`);
          offerData.TownOffer = offer.offerValue; // Send AV value directly
          offerData.LiveOffer = 1; // Town offers are always live
        } else {
          // logger.log(`🔄 [BATCH UPDATE] Sending Aventine offer AV ${offer.offerValue} directly to backend`);
          offerData.OfferValue = offer.offerValue; // Send AV value directly
          // Training users always have LiveOffer = 0, others use passed value
          offerData.LiveOffer = userAccess?.level === 'Training' ? 0 : (offer.liveOffer ?? liveOffer);
        }

        // Add PortfolioOffer if it exists
        if (offer.portfolioOffer !== undefined) {
          // logger.log(`🔄 [BATCH UPDATE] Sending Portfolio offer AV ${offer.portfolioOffer} directly to backend`);
          offerData.PortfolioOffer = offer.portfolioOffer; // Send AV value directly
        }

        // Only send Comments when present; omitting preserves existing DB comments on update
        if (offer.comments !== undefined) {
          offerData.Comments = offer.comments;
        }

        return offerData;
      }),
      caseType,
      liveOffer,
      dueDate,
      round,
      offeredBy,
    };

    // logger.log(
    //   '[create_negotiation_offers_batch] request body',
    //   JSON.stringify(batchPayload, null, 2),
    // );
    let response;
    if (skipDbPersist) {
      logger.log('[TownView dev] Skipping create_negotiation_offers_batch — FE store only');
      response = { data: { success: true, inserted_offers: [] } };
    } else {
      response = await axiosInstance.post('/create_negotiation_offers_batch', batchPayload);
    }

    if (response.data.success) {
      // logger.log('✅ [BATCH UPDATE] API call successful, processing frontend update...');
      
      // Update frontend negotiation object if requested
      if (options.updateFrontend !== false) {
        // logger.log('🔄 [BATCH UPDATE] Updating frontend negotiation object...');
        
        try {
          const getNegotiationObj = usePersistedDataStore.getState().negotiationObj;
          const setNegotiationObj = usePersistedDataStore.getState().setNegotiationObj;
          const incrementNegotiationObjVersion = usePersistedDataStore.getState().incrementNegotiationObjVersion;
          
          if (getNegotiationObj && setNegotiationObj) {
            // Update each offer in the frontend
            let updatedNegotiationObj = getNegotiationObj;
            
            for (const offerWithAction of offersToPersist) {
              const { determinedAction, ...offer } = offerWithAction;
              
              
              const caseFieldUpdates = {};
              const offerLiveOffer = offer.liveOffer ?? liveOffer;
              const negotiationsUpdates = {
                liveOffer: offerLiveOffer,
                updateOffer: {
                  offerId: offer.offerId,
                  offeredBy: offer.offeredBy,
                  offerValue: offer.offerValue,
                  comments: offer.comments, // Always pass comments (even if undefined) to preserve existing comments
                  response: offer.response // Always pass response (even if undefined) to preserve existing response
                }
              };
              
              // Update case fields based on offer type
              if (offer.offeredBy === 'Town') {
                caseFieldUpdates.TownOffer = offer.offerValue;
                // Map Comments to TownNotes for Town offers -- NOW USING THE NEGOTIATIONS OBJ and comments instead for multi round display.
                // if (offer.comments !== undefined) {
                //   caseFieldUpdates.TownNotes = offer.comments;
                // }
              } else if (offer.offeredBy === 'Aventine') {
                if (offer.portfolioOffer !== undefined && offer.portfolioOffer !== null) {
                  caseFieldUpdates.PortfolioOffer = offer.portfolioOffer;
                } else {
                  caseFieldUpdates.OfferValue = offer.offerValue;
                }
              }
              
              
              // Use the pre-determined action from earlier
              const shouldInsertOrUpdate = determinedAction;
              // logger.log('📋 [BATCH UPDATE] Using pre-determined action:', shouldInsertOrUpdate);
              
              if (shouldInsertOrUpdate.action === 'insert') {
                // logger.log('➕ [BATCH UPDATE] INSERT action - creating new offer');
                const targetRound = shouldInsertOrUpdate.round || offer.round || options.round;
                
                const insertedOffer = response.data.inserted_offers?.find(
                  inserted => inserted.ParcelID === offer.parcelId && 
                             inserted.OfferedBy === offer.offeredBy && 
                             inserted.Offer === offer.offerValue
                );
                
                logger.log(`🔍 [OFFERID MATCHING] Looking for inserted offer:`, {
                  parcelId: offer.parcelId,
                  offeredBy: offer.offeredBy,
                  avValue: offer.offerValue,
                  foundOffer: insertedOffer
                });
                
                const addOffer = {
                  offeredBy: offer.offeredBy,
                  offerValue: offer.offerValue, // Store AV value directly - frontend now stores AV values
                  round: targetRound,
                  response: offer.response,
                  offerId: insertedOffer?.OfferID || null // Use OfferID from backend if available
                };
                
                logger.log(`🔄 [FRONTEND STORAGE] Storing AV value ${offer.offerValue} directly in frontend`);
                
                // For INSERT actions, NEVER include comments from existing offers
                // This ensures new rounds always start with clean comment slate
                // Only include comments if they are explicitly provided for this specific offer
                if (offer.comments !== undefined && offer.comments !== null && offer.comments.trim() !== '') {
                  addOffer.comments = offer.comments;
                }
                
                negotiationsUpdates.addOffer = addOffer;
                // logger.log('➕ [BATCH UPDATE] Adding offer with value:', negotiationsUpdates.addOffer);
              } else if (shouldInsertOrUpdate.action === 'update') {
                // logger.log('🔄 [BATCH UPDATE] UPDATE action - updating existing offer');
                negotiationsUpdates.updateOffer = {
                  offerId: shouldInsertOrUpdate.offerId,
                  offeredBy: offer.offeredBy,
                  offerValue: offer.offerValue,
                  response: offer.response,
                  comments: offer.comments
                };
                // logger.log('🔄 [BATCH UPDATE] Updating offer:', negotiationsUpdates.updateOffer);
              }
              
              updatedNegotiationObj = updateNegotiationObject(
                updatedNegotiationObj,
                [{ parcel_id: offer.parcelId }],
                {
                  caseFieldUpdates,
                  negotiationsUpdates
                }
              );
              
              
              // logger.log('✅ [BATCH UPDATE] Negotiation object updated for parcel:', offer.parcelId);
              
            }
            
            // logger.log('💾 [BATCH UPDATE] Setting updated negotiation object in store...');
            setNegotiationObj(updatedNegotiationObj);
            incrementNegotiationObjVersion();
            
            
            // logger.log('✅ [BATCH UPDATE] Frontend update completed successfully');
          } else {
            logger.log('⚠️ [BATCH UPDATE] Store functions not available - skipping frontend update');
          }
        } catch (frontendError) {
          logger.error('❌ [BATCH UPDATE] Error updating frontend negotiation object:', frontendError);
          logger.error('❌ [BATCH UPDATE] Frontend error stack:', frontendError.stack);
          logger.error('❌ [BATCH UPDATE] Frontend error details:', {
            message: frontendError.message,
            name: frontendError.name,
            offers: offers.map(o => ({ parcelId: o.parcelId, offeredBy: o.offeredBy }))
          });
          // Don't fail the entire operation if frontend update fails
        }
      } else {
        logger.log('⚠️ [BATCH UPDATE] Frontend update disabled - skipping');
      }
      
      
      const result = {
        success: true,
        message: `Successfully updated ${response.data.processed_offers} offers in batch`,
        data: response.data,
        processedCount: response.data.processed_offers,
        operationsCount: response.data.operations_count
      };
      
      if (context.linkTownSession && batchHasMeaningfulTownOffer(offers)) {
        maybeNotifyTownPortalFirstResponse({
          linkSession: context.linkTownSession,
          action: 'town_offer_update',
          parcelId: offers.find((o) => o.offeredBy === 'Town')?.parcelId,
          negotiatorId: negotiatorForOffers,
        });
      }

      return result;
    } else {
      const errorMsg = response.data.error || 'Batch update failed';
      logger.error('❌ [BATCH UPDATE] API returned error:', {
        success: response.data.success,
        error: errorMsg,
        responseData: response.data
      });
      throw new Error(errorMsg);
    }

  } catch (error) {
    logger.error('❌ [BATCH UPDATE] Error in batch offer update:', error);
    logger.error('❌ [BATCH UPDATE] Error details:', {
      message: error.message,
      name: error.name,
      response: error.response?.data,
      offers: offers?.map(o => ({ parcelId: o.parcelId, offeredBy: o.offeredBy })) || 'No offers'
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Batch update failed'
    };
  }
};


